import React, { useCallback, useEffect, useRef, useState } from 'react';

// Google Identity Services. Loaded on demand rather than from a <script>
// tag in index.html so every other page in the app - the whole
// authenticated experience - never pays for it.
const GSI_SRC = 'https://accounts.google.com/gsi/client';

// Build-time, and public by design: the client ID is embedded in the
// rendered button and visible to anyone who opens the page. It is an
// identifier, not a secret (the ID-token flow has no client secret at
// all - see backend/services/google_oauth_service.py). Blank/unset
// means this deployment offers password login only, and this component
// renders nothing.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

let scriptPromise = null;

// One shared promise for the whole app: LoginPage and RegisterPage both
// mount this component, and a user moving between them must not trigger
// a second download or a second window.google definition.
function loadGoogleScript() {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('gsi-load-failed')));
      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later mount retry rather than caching the failure
      // forever - a blocked or flaky first load should not disable
      // Google sign-in for the rest of the session.
      scriptPromise = null;
      reject(new Error('gsi-load-failed'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Renders Google's own "Sign in with Google" button.
 *
 * The button must be drawn by Google's library rather than styled here:
 * its appearance is governed by Google's branding rules, and a
 * hand-rolled lookalike is both a policy problem and a phishing
 * pattern.
 *
 * `onCredential` receives the ID token string. This component never
 * inspects it - the token is opaque here and only means anything after
 * the backend verifies its signature. Reading claims out of it in the
 * browser would invite trusting them.
 */
function GoogleSignInButton({ onCredential, onError, text = 'signin_with' }) {
  const containerRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);

  // Kept in a ref so re-rendering the parent (which happens on every
  // keystroke in the sibling email/password form) never re-initializes
  // the Google button underneath it.
  const callbackRef = useRef(onCredential);
  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  const handleCredential = useCallback((response) => {
    if (response?.credential) {
      callbackRef.current(response.credential);
    }
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;

    let cancelled = false;

    loadGoogleScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        });

        window.google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text,
          logo_alignment: 'left',
        });
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
    };
  }, [handleCredential, text]);

  // Nothing configured for this deployment - render nothing at all
  // rather than a button that could only ever fail.
  if (!GOOGLE_CLIENT_ID) return null;

  if (unavailable) {
    return (
      <div className="form-alert" role="status">
        Google Sign-In could not be loaded. You can still sign in with your
        email and password.
      </div>
    );
  }

  return <div className="google-signin-button" ref={containerRef} />;
}

export default GoogleSignInButton;
